import ResponsiveRow from '../shared/ResponsiveRow';
import ResponsiveTable, { ResponsiveCell } from '../shared/ResponsiveTable';
import '../financials/financialsResponsive.css';
import React, { useState, useMemo } from 'react';

export function getPaymentStatusBadge(status) {
    const s = status?.toLowerCase() || '';
    if (['paid','completed'].includes(s)) {
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">Paid</span>;
    }
    if (s === 'pre-authorized' || s === 'pre_authorized') {
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">Pre-Authorized</span>;
    }
    if (s === 'deposit-held' || s === 'deposit_held') {
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">Deposit Held</span>;
    }
    if (s === 'refunded') {
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">Refunded</span>;
    }
    if (s === 'failed') {
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">Failed</span>;
    }
    return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">{status || 'Unknown'}</span>;
}

function PaymentsView({ payments, accounts, theme }) {
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    const formatCurrency = (amount) =>
        new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    };

    const accountMap = useMemo(() => {
        if (!accounts) return {};
        return Object.fromEntries(accounts.map((a) => [a.id, a.account_name || a.name || a.id]));
    }, [accounts]);

    const stats = useMemo(() => {
        if (!payments || payments.length === 0) {
            return { total: 0, paid: 0, depositHeld: 0, preAuthorized: 0, totalAmount: 0, paidAmount: 0 };
        }
        const paid = payments.filter((p) => ['paid','completed'].includes(p.status?.toLowerCase()));
        const depositHeld = payments.filter((p) => ['deposit-held', 'deposit_held'].includes(p.status?.toLowerCase()));
        const preAuth = payments.filter((p) => ['pre-authorized', 'pre_authorized'].includes(p.status?.toLowerCase()));
        const totalAmount = payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
        const paidAmount = paid.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
        return {
            total: payments.length,
            paid: paid.length,
            depositHeld: depositHeld.length,
            preAuthorized: preAuth.length,
            totalAmount,
            paidAmount,
        };
    }, [payments]);

    const filteredPayments = useMemo(() => {
        if (!payments) return [];
        return payments.filter((p) => {
            const accountName = accountMap[p.account_id] || '';
            const matchesSearch =
                !searchTerm ||
                accountName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                p.invoice_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                p.transaction_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                p.payment_method?.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesStatus =
                statusFilter === 'all' || p.status?.toLowerCase() === statusFilter.toLowerCase() || (statusFilter === 'paid' && p.status?.toLowerCase() === 'completed');
            const paymentDate = p.payment_date ? new Date(p.payment_date) : null;
            const matchesDateFrom = !dateFrom || paymentDate === null || paymentDate >= new Date(dateFrom);
            const matchesDateTo = !dateTo || paymentDate === null || paymentDate <= new Date(dateTo + 'T23:59:59');
            return matchesSearch && matchesStatus && matchesDateFrom && matchesDateTo;
        });
    }, [payments, searchTerm, statusFilter, dateFrom, dateTo, accountMap]);

    const isDark = theme === 'dark';

    return (
        <div className={`ia-financials rounded-lg shadow-md p-6 ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
            {/* Header */}
            <div className="mb-6">
                <h3 className={`text-2xl font-bold mb-2 ${'text-[var(--ink)]'}`}>Payments</h3>
                <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>Payment ledger — all recorded payments</p>
            </div>

            {/* Statistics Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-blue-50'}`}>
                    <p className={`text-sm font-semibold ${isDark ? 'text-gray-400' : 'text-blue-600'}`}>Total</p>
                    <p className="text-2xl font-bold text-[var(--ink)]">{stats.total}</p>
                    <p className={`ia-financial-total text-xs ${isDark ? 'text-gray-400' : 'text-blue-700'}`}>{formatCurrency(stats.totalAmount)}</p>
                </div>
                <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-green-50'}`}>
                    <p className={`text-sm font-semibold ${isDark ? 'text-gray-400' : 'text-green-600'}`}>Paid</p>
                    <p className="text-2xl font-bold text-[var(--ink)]">{stats.paid}</p>
                    <p className={`ia-financial-total text-xs ${isDark ? 'text-gray-400' : 'text-green-700'}`}>{formatCurrency(stats.paidAmount)}</p>
                </div>
                <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-yellow-50'}`}>
                    <p className={`text-sm font-semibold ${isDark ? 'text-gray-400' : 'text-yellow-600'}`}>Deposit Held</p>
                    <p className="text-2xl font-bold text-[var(--ink)]">{stats.depositHeld}</p>
                </div>
                <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-indigo-50'}`}>
                    <p className={`text-sm font-semibold ${isDark ? 'text-gray-400' : 'text-indigo-600'}`}>Pre-Authorized</p>
                    <p className="text-2xl font-bold text-[var(--ink)]">{stats.preAuthorized}</p>
                </div>
            </div>

            {/* Filters */}
            <ResponsiveRow className="ia-financial-filters flex flex-col md:flex-row gap-4 mb-6">
                <input
                    type="text"
                    placeholder="Search by account, invoice, transaction ID..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className={`flex-1 px-4 py-2 rounded-lg border ${
                        isDark
                            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                            : 'bg-white border-gray-300 text-gray-900'
                    }`}
                />
                <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className={`px-4 py-2 rounded-lg border ${
                        'bg-[var(--surface-sunken)] border-[var(--surface-border)] text-[var(--ink)]'
                    }`}
                >
                    <option value="all">All Status</option>
                    <option value="paid">Paid</option>
                    <option value="deposit-held">Deposit Held</option>
                    <option value="pre-authorized">Pre-Authorized</option>
                    <option value="refunded">Refunded</option>
                    <option value="failed">Failed</option>
                </select>
                <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    title="From date"
                    className={`px-4 py-2 rounded-lg border ${
                        'bg-[var(--surface-sunken)] border-[var(--surface-border)] text-[var(--ink)]'
                    }`}
                />
                <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    title="To date"
                    className={`px-4 py-2 rounded-lg border ${
                        'bg-[var(--surface-sunken)] border-[var(--surface-border)] text-[var(--ink)]'
                    }`}
                />
            </ResponsiveRow>

            {/* Ledger Table */}
            {filteredPayments.length === 0 ? (
                <div className="text-center py-12">
                    <div className="text-6xl mb-4">💵</div>
                    <p className={`text-lg font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        {payments && payments.length > 0 ? 'No payments match your filters' : 'No payments recorded yet'}
                    </p>
                    <p className={isDark ? 'text-gray-400' : 'text-gray-500'}>
                        {payments && payments.length > 0 ? 'Try adjusting your search or filters' : 'Payments will appear here once recorded'}
                    </p>
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <ResponsiveTable className="w-full">
                        <thead className={isDark ? 'bg-gray-700' : 'bg-gray-50'}>
                            <tr>
                                <th className={`px-4 py-3 text-left text-xs font-semibold ${isDark ? 'text-gray-300' : 'text-gray-600'} uppercase tracking-wider`}>Date</th>
                                <th className={`px-4 py-3 text-left text-xs font-semibold ${isDark ? 'text-gray-300' : 'text-gray-600'} uppercase tracking-wider`}>Account</th>
                                <th className={`px-4 py-3 text-left text-xs font-semibold ${isDark ? 'text-gray-300' : 'text-gray-600'} uppercase tracking-wider`}>Invoice</th>
                                <th className={`px-4 py-3 text-left text-xs font-semibold ${isDark ? 'text-gray-300' : 'text-gray-600'} uppercase tracking-wider`}>Amount</th>
                                <th className={`px-4 py-3 text-left text-xs font-semibold ${isDark ? 'text-gray-300' : 'text-gray-600'} uppercase tracking-wider`}>Method</th>
                                <th className={`px-4 py-3 text-left text-xs font-semibold ${isDark ? 'text-gray-300' : 'text-gray-600'} uppercase tracking-wider`}>Status</th>
                                <th className={`px-4 py-3 text-left text-xs font-semibold ${isDark ? 'text-gray-300' : 'text-gray-600'} uppercase tracking-wider`}>Transaction ID</th>
                            </tr>
                        </thead>
                        <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                            {filteredPayments.map((payment) => (
                                <tr key={payment.id} className={`transition-colors ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}>
                                    <ResponsiveCell label="Date" className={`px-4 py-3 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                        {formatDate(payment.payment_date)}
                                    </ResponsiveCell>
                                    <ResponsiveCell label="Account" className={`px-4 py-3 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                        {accountMap[payment.account_id] || payment.account_id || '—'}
                                    </ResponsiveCell>
                                    <ResponsiveCell label="Invoice" className={`px-4 py-3 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                        {payment.invoice_id || '—'}
                                    </ResponsiveCell>
                                    <ResponsiveCell label="Amount" className={`ia-financial-amount px-4 py-3 text-sm font-semibold ${'text-[var(--ink)]'}`}>
                                        {formatCurrency(payment.amount)}
                                    </ResponsiveCell>
                                    <ResponsiveCell label="Method" className={`px-4 py-3 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                        {payment.payment_method || '—'}
                                    </ResponsiveCell>
                                    <ResponsiveCell label="Status" className="px-4 py-3 text-sm">
                                        {getPaymentStatusBadge(payment.status)}
                                    </ResponsiveCell>
                                    <ResponsiveCell label="Transaction ID" className={`px-4 py-3 text-sm font-mono ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                        {payment.transaction_id || '—'}
                                    </ResponsiveCell>
                                </tr>
                            ))}
                        </tbody>
                    </ResponsiveTable>
                </div>
            )}
        </div>
    );
}

export default PaymentsView;
